import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const EXPENSE_TYPES_LIST_CACHE_KEY = "get-expense-types:";

export const GetExpensesTypes = createAsyncThunk("expensesType/GetExpensesTypes", async ({ token, filters = {} } = {}, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const searchParams = new URLSearchParams();
        Object.entries(filters || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return;
            searchParams.append(key, value);
        });
        const query = searchParams.toString();
        const url = `${config.apiUrl}/expense-types${query ? `?${query}` : ""}`;

        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[GetExpensesTypes] url=', url);
        }

        const data = await coalesceAsync(
            `get-expense-types:${query}`,
            async () => {
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    }

                })

                const body = await response.json();
                if (!response.ok) {
                    const error = new Error(body?.message ?? 'Unable to fetch expense types');
                    error.payload = body;
                    throw error;
                }

                return body;
            },
            { ttlMs: 1500 }
        );

        return fulfillWithValue(data)
    } catch (error) {
        if (error?.payload) {
            return rejectWithValue(error.payload)
        }

        return rejectWithValue({ message: error?.message ?? 'Unable to fetch expense types' })
    }
})

export const AddExpensesType = createAsyncThunk("expensesType/AddExpensesType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/expense-types`, {
            method: "POST",
            body: JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            invalidateCoalescedAsync(EXPENSE_TYPES_LIST_CACHE_KEY)
            return fulfillWithValue(data)
        }
    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const UpdateExpensesType = createAsyncThunk("expensesType/UpdateExpensesType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/expense-types/${obj.id}`, {
            method: "PUT",
            body: JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            invalidateCoalescedAsync(EXPENSE_TYPES_LIST_CACHE_KEY)
            return fulfillWithValue(data)
        }


    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const DeleteExpensesType = createAsyncThunk("expensesType/DeleteExpensesType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/expense-types/${obj.id}`, {
            method: "DELETE",
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            return fulfillWithValue(data)
        }


    } catch (error) {
        throw rejectWithValue(error.message)
    }
})

export const ExpensesTypeSlice = createSlice({
    name: "expensesType",
    initialState: {
        expensesType: null,
        error: null,
        loading: false,
        successMessage: null,
        selectedType: null
    },

    reducers: {
        selectType: (state, action) => {
            state.selectedType = action.payload
        },
        clearExpenseTypeFeedback: (state) => {
            state.error = null
            state.successMessage = null
        }
    },

    extraReducers: (builder) => {
        builder
            .addCase(GetExpensesTypes.pending, (state) => {
                state.loading = true
                state.successMessage = null
                state.error = null
            })
            .addCase(GetExpensesTypes.fulfilled, (state, action) => {
                state.loading = false
                state.expensesType = action.payload.data
                state.successMessage = null
                state.error = null
            })
            .addCase(GetExpensesTypes.rejected, (state) => {
                state.loading = false
                state.successMessage = null
                state.error = null
            })
            .addCase(AddExpensesType.pending, (state) => {
                state.loading = true
                state.successMessage = null
                state.error = null
            })
            .addCase(AddExpensesType.fulfilled, (state, action) => {
                state.loading = false
                state.expensesType = [...state.expensesType, action.payload?.data]
                state.successMessage = action.payload.data.message
                state.successMessage = action.payload.message
                state.error = null
            })
            .addCase(AddExpensesType.rejected, (state, action) => {
                state.loading = false
                state.successMessage = null
                state.error = formatApiError(action.payload, 'Something went wrong')
            })
            .addCase(UpdateExpensesType.pending, (state) => {
                state.loading = true
                state.successMessage = null
                state.error = null
            })
            .addCase(UpdateExpensesType.fulfilled, (state, action) => {
                state.loading = false
                const index = state.expensesType.findIndex(type => type.id === action.payload.data.id);
                state.expensesType[index] = {
                    ...state.expensesType[index],
                    ...action.payload.data,
                };
                state.successMessage = action.payload.message
                state.error = null
            })
            .addCase(UpdateExpensesType.rejected, (state, action) => {
                state.loading = false
                state.successMessage = null
                state.error = formatApiError(action.payload, 'Something went wrong')
            })
            .addCase(DeleteExpensesType.pending, (state) => {
                state.loading = true
                state.successMessage = null
                state.error = null
            })
            .addCase(DeleteExpensesType.fulfilled, (state, action) => {
                state.loading = false
                let index = state.expensesType.findIndex(({ id }) => id === action.payload.data?.id);
                state.expensesType.splice(index, 1);
                state.successMessage = action.payload.message
                state.error = null
            })
            .addCase(DeleteExpensesType.rejected, (state, action) => {
                state.loading = false
                state.successMessage = null
                state.error = formatApiError(action.payload, 'Something went wrong')
            })
    }
})

export default ExpensesTypeSlice.reducer;
export const { selectType, clearExpenseTypeFeedback } = ExpensesTypeSlice.actions